import type { ReactElement } from "react";
import { BrowserRouter } from "react-router-dom";
import type { PublicClientApplication } from "@azure/msal-browser";
import type { AppConfig } from "@shell/auth/authConfig";
import { ExperienceShell } from "@shell/ExperienceShell";

export interface AppProps {
    readonly msalInstance: PublicClientApplication;
    readonly config: AppConfig;
}

// React Router wants a path basename without a trailing slash; Vite's
// BASE_URL always has one. Normalising here keeps the router in lockstep
// with the Vite `base` setting and the Traefik PathPrefix.
const ROUTER_BASENAME = import.meta.env.BASE_URL.replace(/\/$/, "");

export function App({ msalInstance, config }: AppProps): ReactElement {
    return (
        <BrowserRouter basename={ROUTER_BASENAME}>
            <style>{`
                html, body {
                    margin: 0;
                    padding: 0;
                    height: 100%;
                    overflow: hidden;
                    background-color: oklch(0.12 0.005 260);
                    color: oklch(0.95 0 0);
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                }

                /*
                 * #root is locked to the dynamic viewport height — not a
                 * minimum — so the inner flex chain has a hard ceiling to
                 * distribute against. Without this, pages that nest a
                 * scrollable list inside a flex column (Assets, Workspaces,
                 * Assignments, …) lose their per-pane scroll regions: the
                 * document itself grows past the viewport, the body becomes
                 * the scroller, the list can't scroll independently of the
                 * detail pane, and the bottom tab bar / action bar end up
                 * below the fold instead of pinned in the thumb zone.
                 *
                 * 100dvh (not 100vh) so the layout stays correct across
                 * iOS Safari's URL-bar collapse: the chrome reveal/hide
                 * doesn't yank the bottom bars off-screen mid-scroll.
                 */
                #root {
                    margin: 0;
                    padding: 0;
                    height: 100dvh;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                * { box-sizing: border-box; }

                ::selection {
                    background: oklch(0.65 0.15 250);
                    color: white;
                }
            `}</style>
            <ExperienceShell msalInstance={msalInstance} config={config} />
        </BrowserRouter>
    );
}
